/**
 * Resolves a verbatim text quote to a ProseMirror `{from, to}` range inside a
 * Tiptap doc — used to anchor agent comments as text highlights without a live
 * editor instance (the brief editor may not even be mounted when the review
 * runs, so this replicates ProseMirror's position arithmetic over the JSON).
 *
 * Position model (matches prosemirror-model):
 *  - the doc's children start at position 0;
 *  - entering/leaving any element node costs 1 each (open/close tokens);
 *  - a text node occupies one position per character;
 *  - childless inline leaves (hardBreak, image, mention) occupy 1 position.
 *
 * The flattened text mirrors `tiptapToPlainText` (paragraphs separated by a
 * newline) so quotes produced by the LLM from `contentText` match verbatim.
 */

export interface TiptapTextRange {
  from: number;
  to: number;
}

interface TiptapNode {
  type?: string;
  text?: string;
  content?: TiptapNode[];
}

/** Inline nodes that are size 1 and have no open/close tokens. */
// eslint-disable-next-line i18next/no-literal-string -- ProseMirror node type names
const INLINE_LEAF_TYPES = new Set(['hardBreak', 'image', 'mention']);

function nodeSize(node: TiptapNode): number {
  if (node.type === 'text') return node.text?.length ?? 0;
  if (INLINE_LEAF_TYPES.has(node.type ?? '')) return 1;
  const children = node.content ?? [];
  return 2 + children.reduce((sum, child) => sum + nodeSize(child), 0);
}

export function findTextRangeInTiptapDoc(
  docNode: Record<string, unknown> | null | undefined,
  quote: string,
): TiptapTextRange | null {
  const needle = quote.trim();
  if (!docNode || !needle) return null;

  // Flatten the doc into characters plus the ProseMirror position of each.
  // Paragraph separators get a sentinel position (-1): they exist only so the
  // needle's newlines line up; a match never starts or ends on one (trimmed).
  const chars: string[] = [];
  const positions: number[] = [];

  const walkChildren = (children: TiptapNode[], basePos: number): void => {
    let pos = basePos;
    for (const child of children) {
      if (child.type === 'text') {
        const t = child.text ?? '';
        for (let i = 0; i < t.length; i += 1) {
          chars.push(t.charAt(i));
          positions.push(pos + i);
        }
        pos += t.length;
        continue;
      }
      if (!INLINE_LEAF_TYPES.has(child.type ?? '')) {
        walkChildren(child.content ?? [], pos + 1);
        if (child.type === 'paragraph') {
          chars.push('\n');
          positions.push(-1);
        }
      }
      pos += nodeSize(child);
    }
  };

  const docChildren = (docNode as TiptapNode).content ?? [];
  walkChildren(docChildren, 0);

  const haystack = chars.join('');
  const start = haystack.indexOf(needle);
  if (start === -1) return null;

  const from = positions[start];
  const lastPos = positions[start + needle.length - 1];
  if (from < 0 || lastPos < 0) return null;
  return { from, to: lastPos + 1 };
}
